"use client";

import Image from "next/image";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { Asset } from "@/lib/landing-data";

interface AssetIconProps {
  asset: Pick<Asset, "symbol" | "logo" | "color"> & {
    iconBackground?: Asset["iconBackground"];
    kind?: Asset["kind"];
  };
  size?: number;
  className?: string;
  /** Add a faint colored glow ring behind the icon. */
  glow?: boolean;
}

/**
 * Token / stock / chain icon that gracefully degrades to a colored initials
 * chip if the remote logo fails to load (curated logos live on third-party
 * CDNs, so this keeps the page bulletproof). Uses `unoptimized` so we don't
 * route external brand logos through the Next image optimizer.
 */
export function AssetIcon({ asset, size = 32, className, glow = false }: AssetIconProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(asset.logo) && !failed;
  const isStockLogo = showImage && asset.kind === "stock";
  const isPaddedLogo = isStockLogo || asset.symbol === "SOL";
  const chipBackground = asset.iconBackground ?? asset.color;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        className,
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: chipBackground,
        boxShadow: glow ? `0 0 ${size * 0.5}px ${asset.color}55` : undefined,
      }}
    >
      {showImage ? (
        <Image
          src={asset.logo as string}
          alt={asset.symbol}
          width={size}
          height={size}
          unoptimized
          onError={() => setFailed(true)}
          className={cn("h-full w-full", isPaddedLogo ? "object-contain p-[18%]" : "object-cover")}
        />
      ) : (
        <span className="font-semibold leading-none text-white" style={{ fontSize: size * 0.34 }}>
          {asset.symbol.slice(0, 3)}
        </span>
      )}
    </span>
  );
}
